import { AVAILABLE_FONTS } from '../../utils/fontHelper';
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import UnifiedHeroBanner, { CAROUSEL_SIZE_PRESETS, CAROUSEL_WIDTH_MODES } from './UnifiedHeroBanner';
import { getImageCandidateUrls } from '../../utils/imageHelper';

const BannerSlidePreviewImage = ({ url, alt }) => {
  const candidateUrls = useMemo(() => (url ? getImageCandidateUrls(url) : []), [url]);
  const [candidateIdx, setCandidateIdx] = useState(0);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setCandidateIdx(0);
    setHasError(false);
  }, [url]);

  const handleError = () => {
    if (candidateIdx + 1 < candidateUrls.length) {
      setCandidateIdx(prev => prev + 1);
    } else {
      setHasError(true);
    }
  };

  const currentUrl = !hasError && candidateUrls.length > 0 ? candidateUrls[candidateIdx] : null;

  if (!url || !currentUrl) {
    return (
      <div className="w-100 h-100 d-flex flex-column align-items-center justify-content-center bg-light text-muted p-2">
        <i className="bi bi-image fs-3 text-secondary opacity-50"></i>
        <span className="small text-secondary mt-1" style={{ fontSize: '0.72rem' }}>Sin imagen</span>
      </div>
    );
  }

  return (
    <img
      src={currentUrl}
      alt={alt || 'Banner'}
      className="object-fit-cover w-100 h-100"
      onError={handleError}
    />
  );
};

export const UnifiedBannersAdminSection = ({
  banners = [],
  carouselSettings = {},
  onBannersChange,
  onCarouselSettingsChange,
  companyId = 1,
  showLivePreview = true,
  title = 'Banners Principales y Carrusel de Portada',
  subtitle = 'Este formato y proporción se aplica de forma unificada tanto a la Portada de la Web como a la Tienda Online.'
}) => {
  const [uploadingIndex, setUploadingIndex] = useState(null);
  const [isAddingUploading, setIsAddingUploading] = useState(false);

  // Subir imagen para un banner existente
  const handleUploadBannerImage = async (e, index) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('image', file);
    formData.append('company_id', companyId);
    formData.append('target_type', 'banner');

    setUploadingIndex(index);
    try {
      const res = await axios.post('/api/ecommerce/upload-banner/', formData, {
        headers: { 'Content-Type': 'multipart/form-data', 'X-Company-ID': String(companyId) }
      });
      const url = res.data.relative_url || res.data.url;
      const updated = [...banners];
      updated[index] = { ...updated[index], src: url };
      onBannersChange(updated);

      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'success',
        title: 'Imagen de banner actualizada',
        showConfirmButton: false,
        timer: 1800
      });
    } catch (err) {
      console.error('Error al subir banner:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error de carga',
        text: err.response?.data?.error || 'No se pudo subir la imagen.',
        confirmButtonColor: '#dc3545'
      });
    } finally {
      setUploadingIndex(null);
      e.target.value = '';
    }
  };

  // Subir imagen y crear nuevo banner directamente
  const handleAddNewWithUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('image', file);
    formData.append('company_id', companyId);
    formData.append('target_type', 'banner');

    setIsAddingUploading(true);
    try {
      const res = await axios.post('/api/ecommerce/upload-banner/', formData, {
        headers: { 'Content-Type': 'multipart/form-data', 'X-Company-ID': String(companyId) }
      });
      const url = res.data.relative_url || res.data.url;
      const newSlide = {
        id: `banner-${Date.now()}`,
        src: url,
        tag: '',
        title: '',
        subtitle: '',
        buttonText: '',
        buttonLink: '',
        secondaryButtonText: '',
        secondaryButtonLink: '',
        is_active: true,
        order: banners.length + 1
      };
      onBannersChange([...banners, newSlide]);

      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'success',
        title: 'Nuevo banner agregado exitosamente',
        showConfirmButton: false,
        timer: 2000
      });
    } catch (err) {
      console.error('Error al subir nuevo banner:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error de carga',
        text: err.response?.data?.error || 'No se pudo subir la imagen.',
        confirmButtonColor: '#dc3545'
      });
    } finally {
      setIsAddingUploading(false);
      e.target.value = '';
    }
  };

  // Modificar campo de un banner
  const handleFieldChange = (index, field, value) => {
    const updated = [...banners];
    updated[index] = { ...updated[index], [field]: value };
    onBannersChange(updated);
  };

  // Mover banner
  const handleMove = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= banners.length) return;
    const list = [...banners];
    const [moved] = list.splice(index, 1);
    list.splice(newIndex, 0, moved);
    list.forEach((b, i) => { b.order = i + 1; });
    onBannersChange(list);
  };

  // Agregar banner en blanco
  const handleAddBlank = () => {
    const newSlide = {
      id: `banner-${Date.now()}`,
      src: '',
      tag: '',
      title: '',
      subtitle: '',
      buttonText: '',
      buttonLink: '',
      secondaryButtonText: '',
      secondaryButtonLink: '',
      is_active: true,
      order: banners.length + 1
    };
    onBannersChange([...banners, newSlide]);
  };

  // Eliminar banner
  const handleDelete = (index) => {
    Swal.fire({
      title: '¿Eliminar este banner?',
      text: 'No se mostrará ni en la portada ni en la tienda.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        const list = banners.filter((_, i) => i !== index);
        list.forEach((b, i) => { b.order = i + 1; });
        onBannersChange(list);
      }
    });
  };

  const currentSizePreset = carouselSettings?.sizePreset || 'standard';
  const currentWidthMode = carouselSettings?.widthMode || 'container';
  const currentFitMode = carouselSettings?.fitMode || 'cover';
  const currentBorderRadius = carouselSettings?.borderRadius || '16px';

  return (
    <div className="d-flex flex-column gap-4">

      {/* 1. SECCIÓN DE MEDIDAS, FORMATO Y BORDES UNIFICADOS */}
      <div className="card border-0 shadow-sm rounded-4 bg-white">
        <div className="card-header bg-white py-3 px-4 border-0">
          <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between gap-2">
            <div>
              <h5 className="fw-bold mb-0 text-dark d-flex align-items-center gap-2">
                <i className="bi bi-aspect-ratio text-primary"></i>
                {title}
              </h5>
              <small className="text-muted">
                {subtitle}
              </small>
            </div>
            <span className="badge bg-primary-subtle text-primary px-3 py-1.5 rounded-pill fw-bold">
              {CAROUSEL_SIZE_PRESETS[currentSizePreset]?.label || 'Estándar'}
            </span>
          </div>
        </div>

        <div className="card-body p-4 pt-0">
          {/* Selector de Presets */}
          <div className="mb-4">
            <label className="form-label fw-bold text-dark mb-2 small text-uppercase tracking-wider">
              1. Formato y Altura Predeterminada
            </label>
            <div className="row g-3">
              {Object.values(CAROUSEL_SIZE_PRESETS).map((preset) => {
                const isSelected = currentSizePreset === preset.id;
                return (
                  <div key={preset.id} className="col-12 col-md-6 col-lg-4">
                    <div
                      className={`p-3 rounded-4 border cursor-pointer h-100 transition-all ${
                        isSelected
                          ? 'border-primary bg-primary-subtle shadow-sm'
                          : 'border-light bg-light bg-opacity-50'
                      }`}
                      onClick={() => onCarouselSettingsChange('sizePreset', preset.id)}
                      style={{
                        borderWidth: isSelected ? '2px' : '1px',
                        cursor: 'pointer'
                      }}
                    >
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <span className="fw-bold text-dark small">{preset.label}</span>
                        <span className={`badge rounded-pill ${isSelected ? 'bg-primary text-white' : 'bg-secondary-subtle text-secondary'}`} style={{ fontSize: '0.72rem' }}>
                          {preset.badge}
                        </span>
                      </div>
                      <div
                        className="w-100 bg-white border rounded-3 mb-2 d-flex align-items-center justify-content-center text-muted small fw-semibold"
                        style={{
                          aspectRatio: preset.aspectRatio,
                          maxHeight: '65px',
                          overflow: 'hidden'
                        }}
                      >
                        <span style={{ fontSize: '0.74rem' }}>{preset.recommendedResolution}</span>
                      </div>
                      <p className="text-muted small mb-0" style={{ fontSize: '0.78rem', lineHeight: '1.3' }}>
                        {preset.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Ancho, Ajuste de Imagen y Bordes */}
          <div className="row g-3 pt-3 border-top">
            <div className="col-12 col-md-4">
              <label className="form-label fw-bold small text-dark mb-1">
                <i className="bi bi-arrows-expand me-1 text-primary"></i> Ancho del Carrusel
              </label>
              <select
                className="form-select form-select-sm fw-semibold rounded-3"
                value={currentWidthMode}
                onChange={(e) => onCarouselSettingsChange('widthMode', e.target.value)}
              >
                {Object.values(CAROUSEL_WIDTH_MODES).map((mode) => (
                  <option key={mode.id} value={mode.id}>
                    {mode.label}
                  </option>
                ))}
              </select>
              <small className="text-muted d-block mt-1" style={{ fontSize: '0.74rem' }}>
                {CAROUSEL_WIDTH_MODES[currentWidthMode]?.description}
              </small>
            </div>

            <div className="col-12 col-md-4">
              <label className="form-label fw-bold small text-dark mb-1">
                <i className="bi bi-crop me-1 text-primary"></i> Ajuste de la Foto
              </label>
              <select
                className="form-select form-select-sm fw-semibold rounded-3"
                value={currentFitMode}
                onChange={(e) => onCarouselSettingsChange('fitMode', e.target.value)}
              >
                <option value="cover">Cubrir Todo (Cover - Sin bordes vacíos)</option>
                <option value="contain">Mostrar Completa (Contain - Sin recortes)</option>
              </select>
              <small className="text-muted d-block mt-1" style={{ fontSize: '0.74rem' }}>
                {currentFitMode === 'cover'
                  ? 'Rellena todo el marco recortando sobrantes.'
                  : 'Muestra la foto 100% íntegra con fondo.'}
              </small>
            </div>

            <div className="col-12 col-md-4">
              <label className="form-label fw-bold small text-dark mb-1">
                <i className="bi bi-square-rounded me-1 text-primary"></i> Esquinas del Carrusel
              </label>
              <select
                className="form-select form-select-sm fw-semibold rounded-3"
                value={currentBorderRadius}
                onChange={(e) => onCarouselSettingsChange('borderRadius', e.target.value)}
              >
                <option value="0px">Rectas (0px)</option>
                <option value="8px">Redondeado Suave (8px)</option>
                <option value="16px">Redondeado Estándar (16px)</option>
                <option value="24px">Redondeado Pronunciado (24px)</option>
              </select>
              <small className="text-muted d-block mt-1" style={{ fontSize: '0.74rem' }}>
                Borde curvo del contenedor de fotos.
              </small>
            </div>
          </div>

          {/* Tiempo de Transición y Controles Laterales */}
          <div className="row g-3 pt-3 mt-1 border-top">
            <div className="col-12 col-md-4">
              <label className="form-label fw-bold small text-dark mb-1">
                <i className="bi bi-clock-history me-1 text-primary"></i> Tiempo de Cambio Automático
              </label>
              <select
                className="form-select form-select-sm fw-semibold rounded-3"
                value={carouselSettings?.autoPlayInterval !== undefined ? carouselSettings.autoPlayInterval : 5000}
                onChange={(e) => onCarouselSettingsChange('autoPlayInterval', Number(e.target.value))}
              >
                <option value={3000}>3 Segundos (Rápido)</option>
                <option value={4000}>4 Segundos</option>
                <option value={5000}>5 Segundos (Recomendado)</option>
                <option value={6000}>6 Segundos</option>
                <option value={8000}>8 Segundos (Pausado)</option>
                <option value={10000}>10 Segundos (Lento)</option>
                <option value={0}>Desactivado (Solo Manual con Flechas)</option>
              </select>
              <small className="text-muted d-block mt-1" style={{ fontSize: '0.74rem' }}>
                Frecuencia en la que rota de slide automáticamente.
              </small>
            </div>

            <div className="col-12 col-md-4">
              <label className="form-label fw-bold small text-dark mb-1">
                <i className="bi bi-arrow-left-right me-1 text-primary"></i> Botones Laterales (&lt; &gt;)
              </label>
              <div className="form-check form-switch pt-1">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  id="showControlsSwitch"
                  checked={carouselSettings?.showControls !== false}
                  onChange={(e) => onCarouselSettingsChange('showControls', e.target.checked)}
                />
                <label className="form-check-label small fw-semibold text-secondary" htmlFor="showControlsSwitch">
                  {carouselSettings?.showControls !== false ? 'Flechas visibles para pasar fotos' : 'Ocultar flechas laterales'}
                </label>
              </div>
              <small className="text-muted d-block mt-1" style={{ fontSize: '0.74rem' }}>
                Permite al usuario avanzar o retroceder manualmente.
              </small>
            </div>

            <div className="col-12 col-md-4">
              <label className="form-label fw-bold small text-dark mb-1">
                <i className="bi bi-three-dots me-1 text-primary"></i> Puntos Indicadores Inferiores
              </label>
              <div className="form-check form-switch pt-1">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  id="showIndicatorsSwitch"
                  checked={carouselSettings?.showIndicators !== false}
                  onChange={(e) => onCarouselSettingsChange('showIndicators', e.target.checked)}
                />
                <label className="form-check-label small fw-semibold text-secondary" htmlFor="showIndicatorsSwitch">
                  {carouselSettings?.showIndicators !== false ? 'Puntos visibles en el fondo' : 'Ocultar puntos inferiores'}
                </label>
              </div>
              <small className="text-muted d-block mt-1" style={{ fontSize: '0.74rem' }}>
                Indica visualmente la cantidad y orden de slides.
              </small>
            </div>
          </div>

          {/* Opciones de Fondo y Degradé de Imágenes */}
          <div className="row g-3 pt-3 mt-1 border-top">
            <div className="col-12 col-md-6">
              <label className="form-label fw-bold small text-dark mb-1">
                <i className="bi bi-palette me-1 text-primary"></i> Color de Fondo de la Sección
              </label>
              <div className="d-flex align-items-center gap-2">
                <input
                  type="color"
                  className="form-control form-control-color border rounded-3 p-1 cursor-pointer"
                  style={{ width: '42px', height: '36px' }}
                  value={carouselSettings?.backgroundColor || '#f8fafc'}
                  onChange={(e) => onCarouselSettingsChange('backgroundColor', e.target.value)}
                  title="Color de fondo de la sección de banners"
                />
                <input
                  type="text"
                  className="form-control form-control-sm font-monospace fw-semibold"
                  style={{ maxWidth: '120px' }}
                  value={carouselSettings?.backgroundColor || '#f8fafc'}
                  onChange={(e) => onCarouselSettingsChange('backgroundColor', e.target.value)}
                  placeholder="#f8fafc"
                />
                <div className="btn-group btn-group-sm">
                  <button type="button" className="btn btn-outline-secondary py-1 px-2" onClick={() => onCarouselSettingsChange('backgroundColor', '#f8fafc')}>Gris</button>
                  <button type="button" className="btn btn-outline-secondary py-1 px-2" onClick={() => onCarouselSettingsChange('backgroundColor', '#ffffff')}>Blanco</button>
                  <button type="button" className="btn btn-outline-secondary py-1 px-2" onClick={() => onCarouselSettingsChange('backgroundColor', '#0f172a')}>Oscuro</button>
                </div>
              </div>
              <small className="text-muted d-block mt-1" style={{ fontSize: '0.74rem' }}>
                Color detrás del carrusel en la página.
              </small>
            </div>

            <div className="col-12 col-md-6">
              <label className="form-label fw-bold small text-dark mb-1">
                <i className="bi bi-circle-half me-1 text-primary"></i> Degradé / Sombra en Banners (Global)
              </label>
              <div className="form-check form-switch pt-1">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  id="showOverlayGlobalSwitch"
                  checked={carouselSettings?.showOverlay !== false}
                  onChange={(e) => onCarouselSettingsChange('showOverlay', e.target.checked)}
                />
                <label className="form-check-label small fw-semibold text-secondary" htmlFor="showOverlayGlobalSwitch">
                  {carouselSettings?.showOverlay !== false ? 'Degradé oscuro activo (mayor legibilidad de textos)' : 'Sin degradé (imagen 100% limpia)'}
                </label>
              </div>
              <small className="text-muted d-block mt-1" style={{ fontSize: '0.74rem' }}>
                Desactivá si tus banners ya tienen tipografía integrada en el diseño.
              </small>
            </div>

            <div className="col-12 col-md-6">
              <label className="form-label fw-bold small text-dark mb-1">
                <i className="bi bi-magic me-1 text-primary"></i> Efecto de Transición
              </label>
              <select
                className="form-select form-select-sm fw-semibold rounded-3"
                value={carouselSettings?.transitionEffect || 'slide'}
                onChange={(e) => onCarouselSettingsChange('transitionEffect', e.target.value)}
              >
                <option value="slide">Deslizamiento Lateral (Slide tradicional)</option>
                <option value="fade">Desvanecimiento Suave (Fade / Crossfade)</option>
              </select>
              <small className="text-muted d-block mt-1" style={{ fontSize: '0.74rem' }}>
                Animación al pasar de un banner al siguiente.
              </small>
            </div>

            <div className="col-12 col-md-6">
              <label className="form-label fw-bold small text-dark mb-1">
                <i className="bi bi-fonts me-1 text-primary"></i> Tipografía en Banners
              </label>
              <select
                className="form-select form-select-sm fw-semibold rounded-3"
                value={carouselSettings?.fontFamily || 'inherit'}
                onChange={(e) => onCarouselSettingsChange('fontFamily', e.target.value)}
              >
                <option value="inherit">Heredar del sitio (Principal)</option>
                {AVAILABLE_FONTS.map(f => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>
              <small className="text-muted d-block mt-1" style={{ fontSize: '0.74rem' }}>
                Fuente tipográfica utilizada para los títulos y textos del banner.
              </small>
            </div>
          </div>
        </div>
      </div>

      {/* 2. VISTA PREVIA EN VIVO */}
      {showLivePreview && banners.length > 0 && (
        <div className="card border-0 shadow-sm rounded-4 bg-white p-4">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <h6 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2">
              <i className="bi bi-eye-fill text-primary"></i>
              Previsualización del Carrusel Unificado (En Tiempo Real)
            </h6>
            <span className="badge bg-success-subtle text-success px-2.5 py-1 rounded-pill" style={{ fontSize: '0.72rem' }}>
              Portada & Tienda
            </span>
          </div>
          <div className="border rounded-4 p-3 bg-light overflow-hidden">
            <UnifiedHeroBanner
              banners={banners}
              carouselSettings={carouselSettings}
            />
          </div>
        </div>
      )}

      {/* 3. GESTOR DE BANNERS / SLIDERS */}
      <div className="card border-0 shadow-sm rounded-4 bg-white p-4">
        <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between mb-4 gap-2">
          <div>
            <h5 className="fw-bold text-dark mb-1 d-flex align-items-center gap-2">
              <i className="bi bi-images text-success"></i>
              Listado de Banners Unificados ({banners.length})
            </h5>
            <p className="text-muted small mb-0">
              Podés configurar textos y botones superpuestos, o dejar los campos <strong>100% vacíos/limpios</strong> para mostrar banners gráficos puros.
            </p>
          </div>
          <div className="d-flex align-items-center gap-2">
            <label className="btn btn-success btn-sm rounded-pill fw-bold px-3 py-2 shadow-sm d-flex align-items-center gap-2 cursor-pointer mb-0">
              <i className="bi bi-cloud-arrow-up-fill me-1"></i>
              <span>{isAddingUploading ? 'Subiendo...' : 'Subir y Agregar Banner'}</span>
              <input
                type="file"
                accept="image/*"
                onChange={handleAddNewWithUpload}
                disabled={isAddingUploading}
                style={{ display: 'none' }}
              />
            </label>
            <button
              type="button"
              className="btn btn-outline-primary btn-sm rounded-pill fw-bold px-3 py-2 shadow-xs"
              onClick={handleAddBlank}
            >
              <i className="bi bi-plus-lg me-1"></i> Agregar Manual
            </button>
          </div>
        </div>

        {banners.length === 0 ? (
          <div className="text-center py-5 border rounded-4 bg-light">
            <i className="bi bi-images text-muted display-4 d-block mb-3"></i>
            <h6 className="fw-bold text-secondary">No hay banners cargados actualmente.</h6>
            <p className="small text-muted mb-3">Agregá tu primera diapositiva para el carrusel de la web y de la tienda.</p>
            <button type="button" className="btn btn-primary rounded-pill px-4" onClick={handleAddBlank}>
              Agregar Primer Banner
            </button>
          </div>
        ) : (
          <div className="row g-4">
            {banners.map((slide, idx) => {
              const hasText = Boolean(slide.tag || slide.title || slide.subtitle || slide.buttonText || slide.secondaryButtonText);
              const isUploadingThis = uploadingIndex === idx;

              return (
                <div key={slide.id || idx} className="col-12">
                  <div className={`p-4 rounded-4 border transition-all ${slide.is_active !== false ? 'bg-light bg-opacity-50 border-primary-subtle' : 'bg-light opacity-75 border-secondary-subtle'}`}>
                    
                    {/* Encabezado del Slide */}
                    <div className="d-flex flex-wrap align-items-center justify-content-between mb-3 border-bottom pb-2 gap-2">
                      <div className="d-flex align-items-center gap-2">
                        <span className="badge bg-primary text-white fw-bold font-monospace">Banner #{idx + 1}</span>
                        <div className="btn-group btn-group-sm">
                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm px-2 py-0.5"
                            onClick={() => handleMove(idx, -1)}
                            disabled={idx === 0}
                            title="Mover arriba"
                          >
                            <i className="bi bi-chevron-up"></i>
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm px-2 py-0.5"
                            onClick={() => handleMove(idx, 1)}
                            disabled={idx === banners.length - 1}
                            title="Mover abajo"
                          >
                            <i className="bi bi-chevron-down"></i>
                          </button>
                        </div>
                        {hasText ? (
                          <span className="badge bg-info-subtle text-info-emphasis small fw-semibold">
                            <i className="bi bi-card-heading me-1"></i> Con textos y botones superpuestos
                          </span>
                        ) : (
                          <span className="badge bg-secondary-subtle text-secondary small fw-semibold">
                            <i className="bi bi-image me-1"></i> Gráfica limpia (sin textos)
                          </span>
                        )}
                      </div>

                      <div className="d-flex align-items-center gap-3 flex-wrap">
                        <div className="form-check form-switch mb-0" title="Activar o desactivar degradé oscuro sobre esta foto">
                          <input
                            className="form-check-input cursor-pointer"
                            type="checkbox"
                            role="switch"
                            id={`bannerOverlay_${idx}`}
                            checked={slide.showOverlay !== false}
                            onChange={(e) => handleFieldChange(idx, 'showOverlay', e.target.checked)}
                          />
                          <label className="form-check-label small fw-semibold text-muted cursor-pointer" htmlFor={`bannerOverlay_${idx}`}>
                            {slide.showOverlay !== false ? 'Con degradé' : 'Sin degradé'}
                          </label>
                        </div>
                        <div className="form-check form-switch mb-0">
                          <input
                            className="form-check-input cursor-pointer"
                            type="checkbox"
                            role="switch"
                            id={`bannerActive_${idx}`}
                            checked={slide.is_active !== false}
                            onChange={(e) => handleFieldChange(idx, 'is_active', e.target.checked)}
                          />
                          <label className="form-check-label small fw-semibold text-muted cursor-pointer" htmlFor={`bannerActive_${idx}`}>
                            {slide.is_active !== false ? 'Activo' : 'Oculto'}
                          </label>
                        </div>
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm rounded-pill px-2.5"
                          onClick={() => handleDelete(idx)}
                          disabled={banners.length <= 1}
                          title="Eliminar banner"
                        >
                          <i className="bi bi-trash3 me-1"></i> Borrar
                        </button>
                      </div>
                    </div>

                    <div className="row g-3">
                      {/* Imagen y Carga */}
                      <div className="col-lg-4 col-12">
                        <label className="form-label small fw-bold text-muted mb-1">Imagen del Banner</label>
                        <div className="ratio ratio-16x9 rounded-3 overflow-hidden border mb-2 bg-light shadow-xs position-relative">
                          <BannerSlidePreviewImage
                            url={slide.src}
                            alt={slide.title || 'Slide'}
                          />
                          {slide.is_active === false && (
                            <div className="position-absolute top-0 start-0 w-100 h-100 bg-dark bg-opacity-50 d-flex align-items-center justify-content-center">
                              <span className="badge bg-danger rounded-pill">Oculto</span>
                            </div>
                          )}
                        </div>
                        <div className="input-group input-group-sm mb-1">
                          <input
                            type="text"
                            className="form-control font-monospace"
                            placeholder="/media/..."
                            value={slide.src || ''}
                            onChange={(e) => handleFieldChange(idx, 'src', e.target.value)}
                          />
                          <label className="btn btn-outline-primary d-inline-flex align-items-center gap-1 cursor-pointer mb-0">
                            <i className="bi bi-upload"></i>
                            <span>{isUploadingThis ? 'Subiendo...' : 'Subir'}</span>
                            <input
                              type="file"
                              hidden
                              accept="image/*"
                              onChange={(e) => handleUploadBannerImage(e, idx)}
                              disabled={isUploadingThis}
                            />
                          </label>
                        </div>
                        {slide.src && (
                          <button
                            type="button"
                            className="btn btn-link btn-sm text-danger text-decoration-none p-0 mb-2 d-inline-flex align-items-center gap-1"
                            style={{ fontSize: '0.75rem' }}
                            onClick={() => handleFieldChange(idx, 'src', '')}
                          >
                            <i className="bi bi-trash3"></i>
                            <span>Quitar imagen</span>
                          </button>
                        )}

                        {/* Enfoque / Posición de la Imagen */}
                        <div className="mt-2">
                          <label className="form-label small fw-bold text-muted mb-1 d-flex align-items-center justify-content-between">
                            <span><i className="bi bi-arrows-move me-1"></i> Enfoque de Imagen</span>
                            <span className="badge bg-light text-muted border font-monospace" style={{ fontSize: '0.68rem' }}>objectPosition</span>
                          </label>
                          <select
                            className="form-select form-select-sm"
                            value={slide.imagePosition || 'center'}
                            onChange={(e) => handleFieldChange(idx, 'imagePosition', e.target.value)}
                          >
                            <option value="center">Centro (Equilibrado)</option>
                            <option value="right">Alineada a la Derecha (Ideal si el texto va a la izq)</option>
                            <option value="left">Alineada a la Izquierda (Ideal si el texto va a la der)</option>
                            <option value="top">Alineada Arriba</option>
                            <option value="bottom">Alineada Abajo</option>
                          </select>
                        </div>
                      </div>

                      {/* Textos y Enlaces Opcionales */}
                      <div className="col-lg-8 col-12">
                        <div className="p-3 bg-white rounded-3 border h-100 d-flex flex-column justify-content-between">
                          <div>
                            <div className="d-flex align-items-center justify-content-between mb-2">
                              <span className="small fw-bold text-dark">
                                <i className="bi bi-type me-1 text-primary"></i> Disposición, Textos y Botones
                              </span>
                              <span className="small text-muted" style={{ fontSize: '0.72rem' }}>
                                Dejar textos en blanco para banner gráfico puro
                              </span>
                            </div>

                            {/* Controles de Disposición, Alineación y Colores */}
                            <div className="row g-2 p-2.5 bg-light rounded-3 border mb-3">
                              <div className="col-12 col-md-4">
                                <label className="form-label small fw-bold text-dark mb-1">
                                  <i className="bi bi-layout-split me-1 text-primary"></i> Formato de Banner
                                </label>
                                <select
                                  className="form-select form-select-sm fw-semibold"
                                  value={slide.layoutMode || 'cover'}
                                  onChange={(e) => handleFieldChange(idx, 'layoutMode', e.target.value)}
                                >
                                  <option value="cover">Superpuesto (Foto completa de fondo)</option>
                                  <option value="split">Dividido (Texto Izq | Foto Derecha)</option>
                                  <option value="split-reverse">Dividido Invertido (Foto Izq | Texto Der)</option>
                                </select>
                              </div>

                              <div className="col-6 col-md-4">
                                <label className="form-label small fw-bold text-dark mb-1">
                                  <i className="bi bi-text-left me-1 text-primary"></i> Alineación de Texto
                                </label>
                                <select
                                  className="form-select form-select-sm fw-semibold"
                                  value={slide.textAlignment || 'left'}
                                  onChange={(e) => handleFieldChange(idx, 'textAlignment', e.target.value)}
                                >
                                  <option value="left">Izquierda</option>
                                  <option value="center">Centro</option>
                                  <option value="right">Derecha</option>
                                </select>
                              </div>

                              <div className="col-6 col-md-4">
                                <label className="form-label small fw-bold text-dark mb-1">
                                  <i className="bi bi-palette2 me-1 text-primary"></i> Color de Textos
                                </label>
                                <select
                                  className="form-select form-select-sm fw-semibold"
                                  value={slide.textColorTheme || 'light'}
                                  onChange={(e) => handleFieldChange(idx, 'textColorTheme', e.target.value)}
                                >
                                  <option value="light">Texto Claro (Blanco)</option>
                                  <option value="dark">Texto Oscuro (Grafito #0f172a)</option>
                                </select>
                              </div>

                              <div className="col-12 col-md-6 mt-2">
                                <label className="form-label small fw-bold text-muted mb-1">
                                  <i className="bi bi-fonts me-1 text-primary"></i> Tipografía de este Slide
                                </label>
                                <select
                                  className="form-select form-select-sm"
                                  value={slide.fontFamily || 'inherit'}
                                  onChange={(e) => handleFieldChange(idx, 'fontFamily', e.target.value)}
                                >
                                  <option value="inherit">Heredar de Banners / Sitio</option>
                                  {AVAILABLE_FONTS.map(f => (
                                    <option key={f.id} value={f.id}>{f.name}</option>
                                  ))}
                                </select>
                              </div>

                              <div className="col-12 col-md-6 mt-2">
                                <label className="form-label small fw-bold text-muted mb-1">
                                  <i className="bi bi-shadows me-1 text-primary"></i> Sombra de Textos
                                </label>
                                <select
                                  className="form-select form-select-sm"
                                  value={slide.textShadow || 'none'}
                                  onChange={(e) => handleFieldChange(idx, 'textShadow', e.target.value)}
                                >
                                  <option value="none">Sin Sombra (Plano y Limpio - Recomendado)</option>
                                  <option value="subtle">Sombra Suave (Mejora contraste)</option>
                                  <option value="strong">Sombra Marcada</option>
                                </select>
                              </div>
                            </div>

                            <div className="row g-2">
                              <div className="col-md-3">
                                <label className="form-label small fw-bold text-muted mb-1">Etiqueta (Badge)</label>
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  value={slide.tag || ''}
                                  onChange={(e) => handleFieldChange(idx, 'tag', e.target.value)}
                                  placeholder="Ej. FABRICACIÓN DIRECTA"
                                />
                              </div>
                              <div className="col-md-5">
                                <label className="form-label small fw-bold text-muted mb-1">Título 1 (Línea Superior)</label>
                                <input
                                  type="text"
                                  className="form-control form-control-sm fw-bold"
                                  value={slide.title || ''}
                                  onChange={(e) => handleFieldChange(idx, 'title', e.target.value)}
                                  placeholder="Ej. Envases, Embalajes y Cartón"
                                />
                              </div>
                              <div className="col-md-2">
                                <label className="form-label small fw-bold text-muted mb-1">Tamaño 1</label>
                                <select
                                  className="form-select form-select-sm"
                                  value={slide.titleFontSize || 'medium'}
                                  onChange={(e) => handleFieldChange(idx, 'titleFontSize', e.target.value)}
                                >
                                  <option value="small">Chico</option>
                                  <option value="medium">Mediano</option>
                                  <option value="large">Grande</option>
                                  <option value="xlarge">Extra Gr.</option>
                                </select>
                              </div>
                              <div className="col-md-2">
                                <label className="form-label small fw-bold text-muted mb-1">Grosor 1</label>
                                <select
                                  className="form-select form-select-sm"
                                  value={slide.titleFontWeight || 'black'}
                                  onChange={(e) => handleFieldChange(idx, 'titleFontWeight', e.target.value)}
                                >
                                  <option value="regular">Normal</option>
                                  <option value="bold">Negrita</option>
                                  <option value="black">Extra Negrita</option>
                                </select>
                              </div>

                              {/* Segundo Título (Línea 2) */}
                              <div className="col-md-7">
                                <label className="form-label small fw-bold text-muted mb-1 d-flex align-items-center justify-content-between">
                                  <span><i className="bi bi-fonts me-1 text-primary"></i> Segundo Título (Línea 2 - Opcional)</span>
                                  <span className="badge bg-light text-muted border font-monospace" style={{ fontSize: '0.65rem' }}>Debajo de Línea 1</span>
                                </label>
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  value={slide.title2 || ''}
                                  onChange={(e) => handleFieldChange(idx, 'title2', e.target.value)}
                                  placeholder="Ej. ¡Con Entrega Inmediata a Todo el País!"
                                />
                              </div>
                              <div className="col-md-2">
                                <label className="form-label small fw-bold text-muted mb-1">Tamaño 2</label>
                                <select
                                  className="form-select form-select-sm"
                                  value={slide.title2FontSize || 'large'}
                                  onChange={(e) => handleFieldChange(idx, 'title2FontSize', e.target.value)}
                                >
                                  <option value="small">Chico</option>
                                  <option value="medium">Mediano</option>
                                  <option value="large">Grande</option>
                                  <option value="xlarge">Extra Gr.</option>
                                </select>
                              </div>
                              <div className="col-md-3">
                                <label className="form-label small fw-bold text-muted mb-1">Grosor 2</label>
                                <div className="d-flex align-items-center gap-1">
                                  <select
                                    className="form-select form-select-sm"
                                    value={slide.title2FontWeight || 'black'}
                                    onChange={(e) => handleFieldChange(idx, 'title2FontWeight', e.target.value)}
                                  >
                                    <option value="regular">Normal</option>
                                    <option value="bold">Negrita</option>
                                    <option value="black">Extra Negrita</option>
                                  </select>
                                  <button
                                    type="button"
                                    className={`btn btn-sm ${slide.title2Highlight ? 'btn-warning text-dark' : 'btn-outline-secondary'} px-2 py-1`}
                                    onClick={() => handleFieldChange(idx, 'title2Highlight', !slide.title2Highlight)}
                                    title="Destacar Segundo Título con color dorado / amarillo"
                                  >
                                    <i className="bi bi-stars"></i>
                                  </button>
                                </div>
                              </div>
                              <div className="col-md-9">
                                <label className="form-label small fw-bold text-muted mb-1">Subtítulo / Bajada Descriptiva</label>
                                <textarea
                                  className="form-control form-control-sm"
                                  rows="2"
                                  value={slide.subtitle || ''}
                                  onChange={(e) => handleFieldChange(idx, 'subtitle', e.target.value)}
                                  placeholder="Texto explicativo o secundario..."
                                ></textarea>
                              </div>
                              <div className="col-md-3">
                                <label className="form-label small fw-bold text-muted mb-1">Tamaño Subtítulo</label>
                                <select
                                  className="form-select form-select-sm"
                                  value={slide.subtitleFontSize || 'medium'}
                                  onChange={(e) => handleFieldChange(idx, 'subtitleFontSize', e.target.value)}
                                >
                                  <option value="small">Chico</option>
                                  <option value="medium">Mediano (Normal)</option>
                                  <option value="large">Grande</option>
                                </select>
                              </div>
                              {/* Botón Principal (Botón 1) */}
                              <div className="col-12 mt-2">
                                <div className="p-2.5 bg-light rounded-3 border">
                                  <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
                                    <div className="form-check form-switch mb-0">
                                      <input
                                        className="form-check-input cursor-pointer"
                                        type="checkbox"
                                        role="switch"
                                        id={`btn1Switch-${idx}`}
                                        checked={slide.showButton1 !== false}
                                        onChange={(e) => handleFieldChange(idx, 'showButton1', e.target.checked)}
                                      />
                                      <label className="form-check-label small fw-bold text-dark cursor-pointer ms-1" htmlFor={`btn1Switch-${idx}`}>
                                        <i className="bi bi-box-arrow-up-right me-1 text-primary"></i> Botón 1 (Principal)
                                      </label>
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                      <span className="small text-muted" style={{ fontSize: '0.75rem' }}>Estilo:</span>
                                      <select
                                        className="form-select form-select-sm py-1"
                                        style={{ width: 'auto', minWidth: '180px' }}
                                        value={slide.button1Style || slide.buttonStyle || 'primary'}
                                        onChange={(e) => {
                                          handleFieldChange(idx, 'button1Style', e.target.value);
                                          handleFieldChange(idx, 'buttonStyle', e.target.value);
                                        }}
                                      >
                                        <option value="primary">Primario (Marca / Azul)</option>
                                        <option value="accent">Acento (Amarillo Destacado)</option>
                                        <option value="dark">Oscuro (Negro Sólido)</option>
                                        <option value="white">Blanco Sólido</option>
                                        <option value="outline-dark">Contorno Oscuro (Borde Negro)</option>
                                        <option value="outline-light">Contorno Claro (Borde Blanco)</option>
                                        <option value="outline-primary">Contorno Primario</option>
                                        <option value="secondary">Secundario (Gris)</option>
                                      </select>
                                    </div>
                                  </div>
                                  {slide.showButton1 !== false && (
                                    <div className="row g-2">
                                      <div className="col-12 col-md-6">
                                        <div className="input-group input-group-sm">
                                          <span className="input-group-text bg-white small text-muted">Texto</span>
                                          <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Ej. Explorar Catálogo"
                                            value={slide.buttonText || ''}
                                            onChange={(e) => handleFieldChange(idx, 'buttonText', e.target.value)}
                                          />
                                        </div>
                                      </div>
                                      <div className="col-12 col-md-6">
                                        <div className="input-group input-group-sm">
                                          <span className="input-group-text bg-white small text-muted">Link</span>
                                          <input
                                            type="text"
                                            className="form-control"
                                            placeholder="/tienda"
                                            value={slide.buttonLink || slide.link || ''}
                                            onChange={(e) => {
                                              handleFieldChange(idx, 'buttonLink', e.target.value);
                                              handleFieldChange(idx, 'link', e.target.value);
                                            }}
                                          />
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Botón Secundario (Botón 2) */}
                              <div className="col-12 mt-2">
                                <div className="p-2.5 bg-light rounded-3 border">
                                  <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
                                    <div className="form-check form-switch mb-0">
                                      <input
                                        className="form-check-input cursor-pointer"
                                        type="checkbox"
                                        role="switch"
                                        id={`btn2Switch-${idx}`}
                                        checked={slide.showButton2 !== false}
                                        onChange={(e) => handleFieldChange(idx, 'showButton2', e.target.checked)}
                                      />
                                      <label className="form-check-label small fw-bold text-dark cursor-pointer ms-1" htmlFor={`btn2Switch-${idx}`}>
                                        <i className="bi bi-link-45deg me-1 text-secondary"></i> Botón 2 (Secundario)
                                      </label>
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                      <span className="small text-muted" style={{ fontSize: '0.75rem' }}>Estilo:</span>
                                      <select
                                        className="form-select form-select-sm py-1"
                                        style={{ width: 'auto', minWidth: '180px' }}
                                        value={slide.button2Style || (slide.textColorTheme === 'dark' ? 'outline-dark' : 'outline-light')}
                                        onChange={(e) => handleFieldChange(idx, 'button2Style', e.target.value)}
                                      >
                                        <option value="outline-dark">Contorno Oscuro (Borde Negro - Visible en Fondos Claros)</option>
                                        <option value="outline-light">Contorno Claro (Borde Blanco - Para Fondos Oscuros)</option>
                                        <option value="dark">Oscuro (Negro Sólido)</option>
                                        <option value="white">Blanco Sólido</option>
                                        <option value="primary">Primario (Marca / Azul)</option>
                                        <option value="accent">Acento (Amarillo Destacado)</option>
                                        <option value="outline-primary">Contorno Primario</option>
                                        <option value="secondary">Secundario (Gris)</option>
                                      </select>
                                    </div>
                                  </div>
                                  {slide.showButton2 !== false && (
                                    <div className="row g-2">
                                      <div className="col-12 col-md-6">
                                        <div className="input-group input-group-sm">
                                          <span className="input-group-text bg-white small text-muted">Texto</span>
                                          <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Ej. Conocé Nuestra Empresa"
                                            value={slide.secondaryButtonText || ''}
                                            onChange={(e) => handleFieldChange(idx, 'secondaryButtonText', e.target.value)}
                                          />
                                        </div>
                                      </div>
                                      <div className="col-12 col-md-6">
                                        <div className="input-group input-group-sm">
                                          <span className="input-group-text bg-white small text-muted">Link</span>
                                          <input
                                            type="text"
                                            className="form-control"
                                            placeholder="/empresa"
                                            value={slide.secondaryButtonLink || ''}
                                            onChange={(e) => handleFieldChange(idx, 'secondaryButtonLink', e.target.value)}
                                          />
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>

                        </div>
                      </div>
                    </div>

                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

    </div>
  );
};

export default UnifiedBannersAdminSection;
